import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, errText } from "../../../lib/api";
import { Button } from "../../../components/ui/button";
import { toast } from "sonner";
import { Move } from "lucide-react";
import { MenuArranger } from "./MenuArranger";

const Dot = ({ n, testid, tone = "bg-[#F5551E] text-white" }) => (n > 0 ? <span data-testid={testid} className={`ml-1.5 inline-flex items-center justify-center text-[10px] font-bold rounded-full h-5 min-w-5 px-1 ${tone}`}>{n}</span> : null);

// Keep saved order, drop removed buttons, and add any new buttons to their default row.
export const mergeLayout = (saved, defTop, defTabs, known) => {
  const ok = (ids) => (ids || []).filter((i) => known.includes(i));
  const top = ok(saved?.top);
  const tabs = ok(saved?.tabs).filter((i) => !top.includes(i));
  const placed = new Set([...top, ...tabs]);
  defTop.forEach((i) => !placed.has(i) && top.push(i));
  defTabs.forEach((i) => !placed.has(i) && tabs.push(i));
  return { top, tabs };
};

const TopButton = ({ it, active, onClick }) => {
  const btn = (
    <Button variant="outline" onClick={it.to ? undefined : onClick} data-testid={it.testid}
      className={`rounded-xl gap-1.5 ${active ? "border-[#F5551E] bg-[#FFF4F0] text-[#F5551E]" : ""}`}>
      {it.icon && <it.icon size={16} className="text-[#F5551E]" />} {it.label}{it.badges}
    </Button>
  );
  return it.to ? <Link to={it.to}>{btn}</Link> : btn;
};

const TabButton = ({ it, active, onClick }) => {
  const cls = `inline-flex items-center h-8 px-3 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${active ? "bg-slate-100 text-[#12234A] shadow-sm" : "text-slate-500 hover:text-[#12234A]"}`;
  const inner = <>{it.label}{it.badges}</>;
  return it.to
    ? <Link to={it.to} className={cls} data-testid={it.testid}>{inner}</Link>
    : <button type="button" role="tab" aria-selected={active} data-state={active ? "active" : "inactive"} onClick={onClick} className={cls} data-testid={it.testid}>{inner}</button>;
};

export function AdminMenu({ items, defTop, defTabs, tab, setTab }) {
  const known = Object.keys(items);
  const [layout, setLayout] = useState(() => mergeLayout(null, defTop, defTabs, known));
  const [arranging, setArranging] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/site/admin-menu").then((r) => setLayout(mergeLayout(r.data, defTop, defTabs, Object.keys(items)))).catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const save = (rows) => {
    setSaving(true);
    api.put("/admin/site/admin-menu", rows).then((r) => { setLayout(mergeLayout(r.data, defTop, defTabs, known)); setArranging(false); toast.success("Layout saved for all admins"); })
      .catch((e) => toast.error(errText(e))).finally(() => setSaving(false));
  };
  const reset = () => {
    setSaving(true);
    api.delete("/admin/site/admin-menu").then(() => { setLayout(mergeLayout(null, defTop, defTabs, known)); setArranging(false); toast.success("Back to the default layout"); })
      .catch((e) => toast.error(errText(e))).finally(() => setSaving(false));
  };
  return (
    <>
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <h1 className="font-display text-2xl font-extrabold text-[#12234A]">Dashboard</h1>
        <div className="flex items-center gap-2 flex-wrap" data-testid="admin-top-row">
          {!arranging && layout.top.map((id) => <TopButton key={id} it={items[id]} active={tab === id} onClick={() => setTab(id)} />)}
          {!arranging && (
            <Button variant="ghost" size="sm" onClick={() => setArranging(true)} data-testid="admin-arrange-btn" title="Rearrange buttons" className="rounded-xl gap-1.5 text-slate-500"><Move size={15} /> Arrange</Button>
          )}
        </div>
      </div>
      {arranging
        ? <MenuArranger layout={layout} items={items} onSave={save} onReset={reset} onCancel={() => setArranging(false)} saving={saving} />
        : (
          <div role="tablist" className="bg-white border border-slate-200 rounded-xl p-1 mb-5 flex flex-wrap gap-0.5" data-testid="admin-tab-row">
            {layout.tabs.map((id) => <TabButton key={id} it={items[id]} active={tab === id} onClick={() => setTab(id)} />)}
          </div>
        )}
    </>
  );
}

export { Dot };
